import React from "react";

import { Button, Form } from "reactstrap";
import { Field, Formik } from "formik";
import { useDispatch } from "react-redux";

import { BEM } from "helpers/BEM.helper";
import { PasswordValidationMessages } from "views/components/forms/PasswordValidationMessages";
import { assertPasswordIsValid } from "helpers/inputValidators/text/password";
import { FetchFormErrors } from "types/FetchFormErrors";
import { isValidEmail } from "helpers/validateEmail";
import { FetchInputFormik } from "views/common/forms/fields/FetchInputFormik";
import { authenticationActions } from "slices/authentication/authentication.actions";

const classes = new BEM({
  block: { name: "RegisterForm" },
  elements: [
    { name: "firstName" },
    { name: "lastName" },
    { name: "email" },
    { name: "phone" },
    { name: "password" },
    { name: "confirmPassword" },
    { name: "submit" },
    { name: "forgotPassword" },
  ],
  prefix: { name: "form" },
});

export type RegisterFormValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
};
type RegisterFormErrors = FetchFormErrors<RegisterFormValues>;

const RegisterForm: React.FC = () => {
  const dispatch = useDispatch();

  return (
    <Formik
      initialValues={{
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
      }}
      onSubmit={(values) => {
        dispatch(authenticationActions.registerAccount(values));
      }}
      isInitialValid={false}
      validateOnChange={true}
      validate={(values) => {
        const errors: RegisterFormErrors = {};
        const requiredFields: (keyof RegisterFormValues)[] = [
          "firstName",
          "lastName",
          "email",
          "phone",
          "password",
          "confirmPassword",
        ];
        requiredFields.forEach((field: keyof RegisterFormValues) => {
          if (!values[field]) {
            errors[field] = "Required";
          }
        });
        if (values.email && !isValidEmail(values.email)) {
          errors.email = "Invalid email address";
        }
        if (values.phone && !/\d{10}/i.test(values.phone)) {
          errors.phone = "Invalid phone number";
        }
        if (!assertPasswordIsValid(values.password)) {
          errors.password = "Enter valid password";
        }
        if (values.password !== values.confirmPassword) {
          errors.confirmPassword = "Passwords must match";
        }

        return errors;
      }}
    >
      {(formProps) => {
        return (
          <Form
            className={classes.getBlockClassNames()}
            onSubmit={formProps.handleSubmit}
          >
            <Field
              className={classes.getElementClassNames("firstName")}
              component={FetchInputFormik}
              label="First Name"
              name="firstName"
              type="text"
            />
            <Field
              className={classes.getElementClassNames("lastName")}
              component={FetchInputFormik}
              label="Last Name"
              name="lastName"
              type="text"
            />
            <Field
              className={classes.getElementClassNames("email")}
              component={FetchInputFormik}
              label="Email"
              name="email"
              type="text"
            />
            <Field
              className={classes.getElementClassNames("phone")}
              component={FetchInputFormik}
              label="Phone (ex: 5555555555)"
              name="phone"
              type="text"
            />
            <Field
              className={classes.getElementClassNames("password")}
              component={FetchInputFormik}
              label="Password"
              name="password"
              type="password"
            />
            <Field
              className={classes.getElementClassNames("confirmPassword")}
              component={FetchInputFormik}
              label="Confirm Password"
              name="confirmPassword"
              type="password"
            />
            <PasswordValidationMessages password={formProps.values.password} />
            <Button
              className={`${classes.getElementClassNames("submit")}`}
              color="info"
              type="submit"
              disabled={!formProps.isValid && !!formProps.touched}
            >
              Get Started
            </Button>
            <div> or </div>
            <div>
              <a
                className={`${classes.getElementClassNames("forgotPassword")}`}
                href="/forgot-password"
              >
                Forgot Password
              </a>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RegisterForm;
